/**
 * Shared core for the How It Works experience (hub + all chapters).
 *
 * Single source of truth for the accent palette mapping, the slide base
 * shape every chapter's slide defs extend, the audio asset paths, and the
 * dark backdrop the whole experience sits on. Layout components (TourShell,
 * TourSlide, WorkflowSlide, SiteSlide) and the hub all import from here —
 * nothing in this module renders.
 */
import { alpha, type Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/** Which theme-derived accent tints a slide's backdrop glow + kicker. */
export type TourAccent = 'sage' | 'moss' | 'amber' | 'slate' | 'night';

/** The fields TourShell itself needs from any chapter's slide type. */
export interface TourSlideBase {
  id: string;
  /** Small eyebrow label — also the progress dot's aria-label. */
  kicker: string;
  accent: TourAccent;
  /** Public path of the slide's artwork/screenshot. */
  image: string;
  /** Alt text for the artwork. */
  alt: string;
}

export const TOUR_AUDIO = {
  music: '/how-it-works/audio/music.mp3',
  whoosh: '/how-it-works/audio/whoosh.mp3',
  click: '/how-it-works/audio/click.mp3',
  chime: '/how-it-works/audio/chime.mp3',
  finale: '/how-it-works/audio/finale.mp3',
} as const;

export const accentColor = (accent: TourAccent, theme: Theme): string => {
  switch (accent) {
    case 'moss':
      return theme.schedule.tentative;
    case 'amber':
      return theme.schedule.maintenance;
    case 'slate':
      return theme.schedule.machineTime;
    case 'night':
      return theme.customColors.appBar;
    case 'sage':
    default:
      return theme.palette.primary.main;
  }
};

/**
 * The near-black sage gradient every tour surface (hub + chapters) sits on.
 * The two dark stops are shades of the appBar token family; they live here —
 * and only here — so the backdrop can't drift between surfaces.
 */
export const tourBackdropSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  backgroundColor: theme.customColors.appBar,
  backgroundImage: `linear-gradient(160deg, ${alpha('#1C241C', 0.95)} 0%, ${alpha(theme.customColors.appBar, 0.6)} 55%, ${alpha('#141A14', 0.98)} 100%)`,
});
